import React, { useState, useRef, useEffect } from 'react';
import { PlayCircle, Download, Save, Trash2, History, Timer, Loader2, Plus, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { NodeTemplatesModal } from '../NodeTemplatesModal';
import { ClearCanvasModal } from '../ClearCanvasModal';
import { SaveOptionsMenu } from '../SaveOptionsMenu';
import { useEditorStore } from '../../lib/editorStore';
import { useAutoSave } from '../../lib/hooks/useAutoSave';

type NodeType = 'agent' | 'task' | 'tool' | 'flow';

interface CanvasToolbarProps {
  onTestFlow: () => void;
  onGenerateCode: () => void;
  onSaveFlow: () => void;
  onClearCanvas: () => void;
  onToggleVersionHistory: () => void;
  isSaving: boolean;
  lastSavedAt: Date | null;
  showVersionHistory: boolean;
  onApplyTemplate: (template: any) => void;
  className?: string;
}

export function CanvasToolbar({
  onTestFlow,
  onGenerateCode,
  onSaveFlow,
  onClearCanvas,
  onToggleVersionHistory,
  isSaving,
  lastSavedAt,
  showVersionHistory,
  onApplyTemplate,
  className = ''
}: CanvasToolbarProps) {
  const [runOption, setRunOption] = useState('once');
  const [showTemplates, setShowTemplates] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    // Add class to body when modal is open
    if (showTemplates) {
      document.body.classList.add('modal-open');
      setIsModalOpen(true);
    } else {
      document.body.classList.remove('modal-open');
      setIsModalOpen(false);
    }
  }, [showTemplates]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [showSaveOptions, setShowSaveOptions] = useState(false);
  const saveOptionsRef = useRef<HTMLDivElement>(null);

  // Initialize auto-save
  useAutoSave();

  // Handle clicking outside of save options menu
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (saveOptionsRef.current && !saveOptionsRef.current.contains(event.target as Node)) {
        setShowSaveOptions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const nodes = useEditorStore(state => state.nodes);
  const clearCanvas = useEditorStore(state => state.clearCanvas);
  const saveError = useEditorStore(state => state.saveError);
  const autoSave = useEditorStore(state => state.autoSave);
  const selectedNodes = nodes.filter(node => node.selected);
  const nodeTypes = Array.from(new Set(nodes.map(node => node.type))) as NodeType[];

  const handleRunFlow = async () => {
    try {
      if (runOption === 'once') {
        await onTestFlow();
      } else {
        setIsScheduling(true);
        // Here you would implement the scheduling logic based on runOption
        // For now, we'll just simulate a delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        // You would typically make an API call to set up the schedule
      }
    } catch (error) {
      console.error('Error running flow:', error);
    } finally {
      setIsScheduling(false);
    }
  };

  const handleGenerateCode = async () => {
    try {
      await onGenerateCode();
    } catch (error) {
      console.error('Error generating code:', error);
    }
  };

  const handleSaveFlow = async () => {
    try {
      await onSaveFlow();
    } catch (error) {
      console.error('Error saving flow:', error);
    }
  };

  const handleClearCanvas = () => {
    setShowClearModal(true);
  };

  const handleConfirmClear = (options: { selectedOnly: boolean; nodeTypes: NodeType[] }) => {
    onClearCanvas();
    clearCanvas(options);
    setShowClearModal(false);
  };

  return (
    <div className={`flex flex-col bg-gray-800/90 backdrop-blur-sm border-b border-gray-700 ${className}`}>
      {/* Collapse Toggle */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-gray-800 border border-gray-700 rounded-full p-0.5 hover:bg-gray-700 transition-all shadow-sm hover:shadow-md z-10"
      >
        {isCollapsed ? (
          <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
        ) : (
          <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
        )}
      </button>

      {/* Toolbar Content */}
      <div className={`transition-all duration-300 ease-in-out ${isCollapsed ? 'h-0 overflow-hidden' : ''}`}>
        <div className="flex items-center justify-between px-4 py-2">
          {/* Left Side - Save Status and Run Options */}
          <div className="flex items-center">
            {/* Save Status */}
            <div className="flex items-center gap-2 px-2 py-1 bg-gray-700/30 rounded-lg mr-2" ref={saveOptionsRef}>
              <button
                onClick={() => setShowSaveOptions(!showSaveOptions)}
                className="flex items-center gap-1 hover:bg-gray-700 rounded-md px-2 py-1 transition-all"
              >
                {isSaving ? (
                  <div className="flex items-center gap-1 text-gray-400">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span className="text-xs">Saving...</span>
                  </div>
                ) : saveError ? (
                  <div className="flex items-center gap-1 text-red-400">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                    <span className="text-xs">{saveError}</span>
                  </div>
                ) : lastSavedAt && (
                  <div className="flex items-center gap-1 text-gray-400">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    <span className="text-xs">
                      Saved {lastSavedAt.toLocaleTimeString()}
                      {autoSave && ' • Auto-save on'}
                    </span>
                  </div>
                )}
                <Settings className="w-3.5 h-3.5 text-gray-400 ml-1" />
              </button>

              {showSaveOptions && (
                <SaveOptionsMenu onClose={() => setShowSaveOptions(false)} />
              )}
            </div>

            {/* Run Options */}
            <div className="flex items-center gap-2 px-2 py-1 bg-gray-700/30 rounded-lg">
              <Timer className="w-3.5 h-3.5 text-gray-400" />
              <select
                value={runOption}
                onChange={(e) => setRunOption(e.target.value)}
                className="bg-gray-700 text-xs text-gray-300 border border-gray-600 rounded-md px-2 py-1 focus:ring-1 focus:ring-indigo-500 focus:border-transparent h-[26px]"
              >
                <option value="once">Run once</option>
                <option value="15min">Every 15 minutes</option>
                <option value="hourly">Every hour</option>
                <option value="daily">Every day</option>
              </select>
            </div>
          </div>

          {/* Right Side - Action Buttons */}
          <div className="flex items-center">
            {/* Primary Actions Group */}
            <div className="flex items-center gap-2 px-2 py-1 bg-gray-700/30 rounded-lg mr-2">
              <button
                onClick={() => setShowTemplates(true)}
                className="flex items-center gap-1 px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-all text-xs shadow-sm hover:shadow-md"
              >
                <Plus className="w-3.5 h-3.5" />
                Templates
              </button>

              <button
                onClick={handleRunFlow}
                disabled={isScheduling}
                className="flex items-center gap-1 px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-all text-xs shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed min-w-[90px] justify-center"
              >
                {isScheduling ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <PlayCircle className="w-3.5 h-3.5" />
                )}
                {runOption === 'once' ? 'Test Flow' : 'Schedule'}
              </button>
            </div>

            {/* Secondary Actions Group */}
            <div className="flex items-center gap-2 px-2 py-1 bg-gray-700/30 rounded-lg mr-2">
              <button
                onClick={handleGenerateCode}
                className="flex items-center gap-1 px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all text-xs shadow-sm hover:shadow-md"
              >
                <Download className="w-3.5 h-3.5" />
                Generate
              </button>

              <button
                onClick={handleSaveFlow}
                disabled={isSaving}
                className={`flex items-center gap-1 px-2 py-1 ${
                  saveError 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-gray-700 hover:bg-gray-600'
                } text-white rounded-md transition-all text-xs shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px] justify-center`}
                title={saveError ? 'Click to retry saving' : 'Save Flow'}
              >
                <Save className="w-3.5 h-3.5" />
                {saveError ? 'Retry' : 'Save'}
              </button>
            </div>

            {/* Utility Actions Group */}
            <div className="flex items-center gap-2 px-2 py-1 bg-gray-700/30 rounded-lg">
              <button
                onClick={handleClearCanvas}
                className="flex items-center gap-1 px-2 py-1 bg-red-600/90 hover:bg-red-700 text-white rounded-md transition-all text-xs shadow-sm hover:shadow-md"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Clear
              </button>

              <button
                onClick={onToggleVersionHistory}
                className={`flex items-center gap-1 px-2 py-1 rounded-md transition-all text-xs shadow-sm hover:shadow-md ${
                  showVersionHistory 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <History className="w-3.5 h-3.5" />
                History
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showTemplates && (
        <NodeTemplatesModal
          onClose={() => setShowTemplates(false)}
          onApplyTemplate={(template) => {
            onApplyTemplate(template);
            setShowTemplates(false);
          }}
        />
      )}
      {showClearModal && (
        <ClearCanvasModal
          onClose={() => setShowClearModal(false)}
          onConfirm={handleConfirmClear}
          nodeTypes={nodeTypes}
          selectedNodesCount={selectedNodes.length}
        />
      )}
    </div>
  );
}
